import { c as _c } from "react/compiler-runtime";
import type { ReactElement } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { SeizeQuoteLinkInfo } from "@/helpers/SeizeLinkParser";
import LinkHandlerFrame from "@/components/waves/LinkHandlerFrame";
import WaveDropQuoteWithDropId from "@/components/waves/drops/WaveDropQuoteWithDropId";
import WaveDropQuoteWithSerialNo from "@/components/waves/drops/WaveDropQuoteWithSerialNo";
import ExpandableTweetPreview from "@/components/tweets/ExpandableTweetPreview";
import type { TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { ensureTwitterLink } from "./twitter";
interface SeizeQuoteRenderOptions {
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const TweetFallback = t0 => {
  const $ = _c(4);
  const {
    href
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-text-sm tw-font-medium tw-text-iron-100">Tweet unavailable</span>;
    t2 = <span className="tw-text-xs tw-text-iron-400">Open on X</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== href) {
    t3 = <a href={href} target="_blank" rel="noopener noreferrer" className="tw-flex tw-h-full tw-w-full tw-flex-col tw-justify-center tw-gap-y-1 tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-p-4 tw-text-left tw-no-underline tw-transition-colors tw-duration-200 hover:tw-border-iron-500 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400">{t1}{t2}</a>;
    $[2] = href;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const renderTweetEmbed = (href: string, options?: {
  readonly tweetPreviewMode?: TweetPreviewMode;
}) => {
  const {
    tweetId,
    href: normalizedHref
  } = ensureTwitterLink(href);
  const renderFallback = () => <TweetFallback href={normalizedHref} />;
  return <LinkHandlerFrame href={normalizedHref} overlayAnchor="content">
      <div className="tw-w-full tw-min-w-0 tw-flex-1 lg:tw-max-w-[480px]">
        <ErrorBoundary fallbackRender={() => renderFallback()}>
          <ExpandableTweetPreview href={normalizedHref} tweetId={tweetId} compactMode={options?.tweetPreviewMode} renderFallback={fallbackHref => <TweetFallback href={fallbackHref} />} />
        </ErrorBoundary>
      </div>
    </LinkHandlerFrame>;
};
const CHAT_GIF_HEIGHT = 180;
interface GifEmbedOptions {
  readonly fixedSize?: boolean | undefined;
}
const renderGifEmbed = (url: string, options?: GifEmbedOptions): ReactElement => {
  if (options?.fixedSize) {
    return (
      // Use fixed height to prevent vertical layout shift while preserving GIF aspect ratio.
      <img src={url} alt="Embedded GIF" className="tw-block tw-w-auto tw-max-w-full tw-rounded-xl tw-object-contain" style={{
        height: `${CHAT_GIF_HEIGHT}px`
      }} loading="lazy" />
    );
  }
  return (
    // Markdown-style GIF embeds intentionally keep <img> for animation support.
    <img src={url} alt="Embedded GIF" className="tw-h-auto tw-max-h-[25rem] tw-max-w-[100%]" loading="lazy" />
  );
};
const renderSeizeQuote = (quoteLinkInfo: SeizeQuoteLinkInfo, onQuoteClick: (drop: ApiDrop) => void, href: string, options?: SeizeQuoteRenderOptions): ReactElement | null => {
  const {
    waveId,
    serialNo,
    dropId
  } = quoteLinkInfo;
  if (serialNo) {
    return <LinkHandlerFrame href={href} hideLink>
        <WaveDropQuoteWithSerialNo serialNo={Number.parseInt(serialNo, 10)} waveId={waveId} onQuoteClick={onQuoteClick} embedPath={options?.embedPath} quotePath={options?.quotePath} embedDepth={options?.embedDepth} maxEmbedDepth={options?.maxEmbedDepth} />
      </LinkHandlerFrame>;
  }
  if (dropId) {
    return <LinkHandlerFrame href={href}>
        <WaveDropQuoteWithDropId dropId={dropId} partId={1} maybeDrop={null} waveId={waveId} onQuoteClick={onQuoteClick} embedPath={options?.embedPath} quotePath={options?.quotePath} embedDepth={options?.embedDepth} maxEmbedDepth={options?.maxEmbedDepth} />
      </LinkHandlerFrame>;
  }
  return null;
};
export { renderGifEmbed, renderSeizeQuote, renderTweetEmbed };